import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { useMemo } from 'react';

import { useSkillsSafe } from '../components/chat-v2/contexts/skills-context';
import { buildSkillCommands } from '../lib/skill-constants';
import { GET_SKILLS_BY_FILTERS } from '../queries/chats';

interface UseSkillCommandsOptions {
  accountUuid?: string;
  userUuid?: string;
}

export function useSkillCommands({ accountUuid, userUuid }: UseSkillCommandsOptions) {
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const skillsContext = useSkillsSafe();

  const skillsQuery = useQuery(GET_SKILLS_BY_FILTERS, {
    variables: { userUuid: userUuid!, accountUuid },
    skip: !isNovaSkillsEnabled || Boolean(skillsContext) || isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const skills = useMemo(
    () => (isNovaSkillsEnabled ? (skillsContext?.skills ?? skillsQuery.data?.skills ?? []) : []),
    [isNovaSkillsEnabled, skillsContext, skillsQuery.data?.skills],
  );
  const commands = useMemo(() => buildSkillCommands(skills), [skills]);
  const isSkillsLoading = isNovaSkillsEnabled ? skillsContext?.isLoading || skillsQuery.loading : false;
  const skillsError = isNovaSkillsEnabled ? (skillsContext?.error ?? skillsQuery.error) : undefined;

  return {
    commands,
    skills,
    isLoading: isSkillsLoading,
    error: skillsError,
  };
}
